import { useState, useEffect } from "react";
import { DurationType, PaymentDetails } from "../../types";
import accessURL from "../../../components/utils/superAccess";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (details: PaymentDetails) => void;
  id: number;
  isLoading?: boolean;
}

const allDurations = [
  { value: "trial", label: "Trial" },
  { value: "1month", label: "1 Month" },
  { value: "2months", label: "2 Months" },
  { value: "3months", label: "3 Months" },
  { value: "4months", label: "4 Months" },
  { value: "5months", label: "5 Months" },
  { value: "6months", label: "6 Months" },
  { value: "7months", label: "7 Months" },
  { value: "8months", label: "8 Months" },
  { value: "9months", label: "9 Months" },
  { value: "10months", label: "10 Months" },
  { value: "11months", label: "11 Months" },
  { value: "1year", label: "1 Year" },
];

export function PaymentModal({
  isOpen,
  onClose,
  onSubmit,
  id,
  isLoading = false,
}: PaymentModalProps) {
  const [details, setDetails] = useState<PaymentDetails>({
    duration: "1month",
    price: "",
  });
  const [durations, setDurations] = useState(allDurations);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && id) {
      fetchPaidStatus();
    }
  }, [isOpen, id]);

  const fetchPaidStatus = async () => {
    setLoading(true);
    try {
      const res = await accessURL.get(`super-admin/paid-status/${id}`);

      if (res.data && res.data.paid_status === "trial") {
        const filteredDurations = allDurations.filter(
          (duration) => duration.value !== "trial"
        );
        setDurations(filteredDurations);
        setDetails((prev) => ({
          ...prev,
          duration: "1month",
        }));
      } else {
        setDurations(allDurations);
      }
    } catch (error) {
      setDurations(allDurations);
      return error;
    }
    setLoading(false);
  };

  const handleDurationChange = (newDuration: DurationType) => {
    setDetails({
      duration: newDuration,
      price: newDuration === "trial" ? "0" : details.price,
    });
  };

  const handleSubmit = () => {
    if (details.duration === "trial") {
      onSubmit({ ...details, price: "0" });
    } else if (!details.price) {
      return;
    } else {
      onSubmit(details);
    }
  };

  if (!isOpen) return null;

  const showPriceInput = details.duration !== "trial";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-50 w-full max-w-md p-6 bg-white rounded-lg shadow-xl">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-gray-900">
            Update Payment Details
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Make changes to the payment duration
            {showPriceInput ? " and price" : ""}.
          </p>
        </div>

        {loading ? (
          <div className="py-4 text-center text-gray-500">
            <div className="flex justify-center">
              <svg
                className="animate-spin h-5 w-5 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <div className="mt-2">Loading options...</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="duration"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Duration
              </label>
              <select
                id="duration"
                value={details.duration}
                onChange={(e) =>
                  handleDurationChange(e.target.value as DurationType)
                }
                className="block w-full rounded-md border-gray-300 border px-3 py-2 text-gray-900 focus:border-blue-500 focus:ring-blue-500 focus:outline-none sm:text-sm"
                disabled={isLoading}
              >
                {durations.map((duration) => (
                  <option key={duration.value} value={duration.value}>
                    {duration.label}
                  </option>
                ))}
              </select>
            </div>

            {showPriceInput && (
              <div>
                <label
                  htmlFor="price"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Price(USD)
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {/* <span className="text-gray-500 sm:text-sm">USD</span> */}
                  </div>
                  <input
                    type="number"
                    id="price"
                    value={details.price}
                    onChange={(e) =>
                      setDetails({
                        ...details,
                        price: e.target.value,
                      })
                    }
                    className="block w-full pl-7 pr-3 py-2 rounded-md border-gray-300 border focus:border-blue-500 focus:ring-blue-500 focus:outline-none sm:text-sm"
                    placeholder="0.00"
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              (!details.price && details.duration !== "trial") ||
              loading ||
              isLoading
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
